import { useState } from 'react';
import { BossMasterSyncBoard } from '../../boss-master-sync/shared/BossMasterSyncBoard.jsx';
import { BroadcastManagementBoard } from '../../broadcast/admin/BroadcastManagementBoard.jsx';
import { NikkeMasterSyncBoard } from '../../nikke-master-sync/shared/NikkeMasterSyncBoard.jsx';
import { PlatformAccountModerationBoard } from '../../platform-account-moderation/shared/PlatformAccountModerationBoard.jsx';
import { PlatformChatModerationBoard } from '../../platform-chat-moderation/shared/PlatformChatModerationBoard.jsx';
import { PlatformStickerManagementBoard } from '../../stickers/admin/PlatformStickerManagementBoard.jsx';
import { PlatformUnionManagementBoard } from '../../platform-union-management/shared/PlatformUnionManagementBoard.jsx';
import { usePlatformUnionReview } from '../hooks/usePlatformUnionReview.js';
import { PlatformManagementMenu } from '../shared/PlatformManagementMenu.jsx';
import '../../../styles/boss-master-sync.css';

const PLATFORM_SECTION_STORAGE_KEY = 'doroparty_platform_management_section';
const PLATFORM_SECTIONS = new Set(['union-review', 'union-management', 'account-moderation', 'stickers', 'nikke-db', 'boss-db', 'broadcast']);

function initialPlatformSection() {
  try {
    const stored = window.sessionStorage.getItem(PLATFORM_SECTION_STORAGE_KEY);
    return PLATFORM_SECTIONS.has(stored) ? stored : 'union-review';
  } catch {
    return 'union-review';
  }
}

function formatRequestedAt(value) {
  if (!value) return '시간 확인 불가';

  try {
    return new Intl.DateTimeFormat('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return '시간 확인 불가';
  }
}

function applicantOpenid(profile) {
  return String(profile?.normalized_openid || profile?.openid || '').trim();
}

const COMMUNITY_TYPE_LABELS = {
  gallery: '갤러리(갤길)',
  channel: '채널(챈길)',
};

function communityTypeLabel(value) {
  return COMMUNITY_TYPE_LABELS[value] || '확인 불가';
}

// 심사가 실제로 판단하는 건 "신청자가 인증한 인게임 길드가 이 유니온이 맞는가"다. RPC가 이미
// applicant_guild_name/applicant_guild_id 를 돌려주는데 화면에서는 버리고 있었다. 대신 비우던
// '신청 역할'은 등록 신청이 전부 주딱이라 항상 같은 값이었다.
function applicantGuildLabel(applicant) {
  const name = String(applicant?.guild_name || '').trim();
  const id = String(applicant?.guild_id || '').trim();
  if (name && id) return `${name} (${id})`;
  return name || id || '인증 정보 없음';
}

// 유니온 활성화는 되돌리기 어렵고 '거절' 바로 옆에 있다. 승인 직전에 무엇을 승인하는지 —
// 특히 인증된 인게임 길드를 — 한 번 더 보여준다. 레포의 다른 관리자 동작과 같은 방식.
function approvalConfirmation(application, applicant) {
  return [
    `'${application.name}' 유니온을 활성화합니다.`,
    `신청자: ${applicant?.nickname || '확인 불가'}`,
    `인게임 길드: ${applicantGuildLabel(applicant)}`,
    '',
    '계속할까요?',
  ].join('\n');
}


export function DesktopPlatformUnionReview() {
  const reviewState = usePlatformUnionReview();
  const [rejectionReasons, setRejectionReasons] = useState({});
  const [activeSection, setActiveSection] = useState(initialPlatformSection);
  const compactEmpty = !reviewState.loading && reviewState.applications.length === 0;

  if (!reviewState.loading && !reviewState.isPlatformAdmin) return null;

  const fullAdminOnlySection = activeSection === 'account-moderation' || activeSection === 'stickers' || activeSection === 'nikke-db' || activeSection === 'boss-db' || activeSection === 'broadcast';
  const effectiveSection = !reviewState.loading && !reviewState.isFullAdmin && fullAdminOnlySection ? 'union-review' : activeSection;

  const selectSection = (section) => {
    if (!PLATFORM_SECTIONS.has(section)) return;
    setActiveSection(section);
    try { window.sessionStorage.setItem(PLATFORM_SECTION_STORAGE_KEY, section); } catch {}
  };

  return (
    <>
      <PlatformManagementMenu
        activeSection={effectiveSection}
        onSelect={selectSection}
        pendingUnionCount={reviewState.applications.length}
        fullAdmin={reviewState.isFullAdmin}
      />

      {effectiveSection === 'union-review' ? (
        <div
          className="platform-management-panel"
          id="platform-management-panel-union-review"
          role="tabpanel"
          aria-labelledby="platform-management-tab-union-review"
        >
          <section className={`desktop-platform-union-review ${compactEmpty ? 'is-empty' : ''}`} aria-label="플랫폼 유니온 등록 심사">
            <div className="desktop-platform-union-review-heading">
              <div>
                <h2>유니온 등록 심사</h2>
                <p>플랫폼 관리자가 주딱 신청과 인증된 유니온 정보를 확인해 활성화합니다.</p>
              </div>
              <div className="desktop-platform-union-review-count">
                <span>대기</span>
                <strong>{reviewState.applications.length}</strong>
              </div>
            </div>

            {reviewState.loading ? (
              <div className="desktop-platform-union-review-loading" role="status">
                <span aria-hidden="true" />
                <p>승인 대기 유니온을 불러오고 있습니다.</p>
              </div>
            ) : reviewState.applications.length === 0 ? (
              <div className="desktop-platform-union-review-empty">
                <strong>승인 대기 유니온 없음</strong>
                <p>새로운 유니온 등록 신청이 접수되면 이곳에 표시됩니다.</p>
              </div>
            ) : (
              <div className="desktop-platform-union-review-list">
                {reviewState.applications.map((application) => {
                  const isProcessing = reviewState.actionUnionId === application.id;
                  const applicant = application.applicantProfile;
                  const openid = applicantOpenid(applicant);
                  const reason = rejectionReasons[application.id] || '';

                  return (
                    <article className="desktop-platform-union-review-card" key={application.id}>
                      <div className="desktop-platform-union-review-card-main">
                        <div className="desktop-platform-union-review-title-row">
                          <div>
                            <h3>{application.name}</h3>
                          </div>
                          <strong>{applicant?.nickname || '신청자 확인 불가'}</strong>
                        </div>

                        <dl>
                          <div>
                            <dt>신청자 식별자</dt>
                            <dd className="identifier">{openid || '확인 불가'}</dd>
                          </div>
                          <div>
                            <dt>인게임 길드</dt>
                            <dd>{applicantGuildLabel(applicant)}</dd>
                          </div>
                          <div>
                            <dt>커뮤니티 유형</dt>
                            <dd>{communityTypeLabel(application.community_type)}</dd>
                          </div>
                          <div>
                            <dt>신청 시각</dt>
                            <dd>{formatRequestedAt(application.requested_at)}</dd>
                          </div>
                        </dl>
                      </div>

                      <div className="desktop-platform-union-review-actions">
                        <label>
                          <span>거절 사유</span>
                          <input
                            type="text"
                            maxLength={500}
                            value={reason}
                            onChange={(event) => {
                              setRejectionReasons((current) => ({
                                ...current,
                                [application.id]: event.target.value,
                              }));
                            }}
                            placeholder="거절할 때만 입력"
                            disabled={Boolean(reviewState.actionUnionId)}
                          />
                        </label>
                        <div>
                          <button
                            className="reject"
                            type="button"
                            disabled={Boolean(reviewState.actionUnionId)}
                            onClick={() => reviewState.review({
                              unionId: application.id,
                              unionName: application.name,
                              approve: false,
                              rejectionReason: reason,
                            })}
                          >
                            {isProcessing ? '처리 중…' : '거절'}
                          </button>
                          <button
                            className="approve"
                            type="button"
                            disabled={Boolean(reviewState.actionUnionId)}
                            onClick={() => {
                              if (!window.confirm(approvalConfirmation(application, applicant))) return;
                              reviewState.review({ unionId: application.id, unionName: application.name, approve: true });
                            }}
                          >
                            {isProcessing ? '처리 중…' : '등록 승인'}
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}

            {reviewState.notice.message ? (
              <p className={`desktop-platform-union-review-notice ${reviewState.notice.type}`} role="status">
                {reviewState.notice.message}
              </p>
            ) : null}
          </section>
        </div>
      ) : null}

      {effectiveSection === 'union-management' ? (
        <div
          className="platform-management-panel"
          id="platform-management-panel-union-management"
          role="tabpanel"
          aria-labelledby="platform-management-tab-union-management"
        >
          <PlatformUnionManagementBoard />
          <PlatformChatModerationBoard />
        </div>
      ) : null}

      {effectiveSection === 'account-moderation' ? (
        <div
          className="platform-management-panel"
          id="platform-management-panel-account-moderation"
          role="tabpanel"
          aria-labelledby="platform-management-tab-account-moderation"
        >
          <section className="desktop-platform-account-moderation-wrap" aria-label="계정 및 식별자 밴 관리">
            <header>
              <h2>계정·식별자 밴 관리</h2>
              <p>가입 계정과 아직 가입하지 않은 DC·아카라이브·게임 식별자의 밴 및 해제를 관리합니다.</p>
            </header>
            <PlatformAccountModerationBoard />
          </section>
        </div>
      ) : null}

      {effectiveSection === 'stickers' ? (
        <div
          className="platform-management-panel"
          id="platform-management-panel-stickers"
          role="tabpanel"
          aria-labelledby="platform-management-tab-stickers"
        >
          <PlatformStickerManagementBoard />
        </div>
      ) : null}

      {effectiveSection === 'nikke-db' ? (
        <div
          className="platform-management-panel"
          id="platform-management-panel-nikke-db"
          role="tabpanel"
          aria-labelledby="platform-management-tab-nikke-db"
        >
          <NikkeMasterSyncBoard />
        </div>
      ) : null}

      {effectiveSection === 'broadcast' ? (
        <div
          className="platform-management-panel"
          id="platform-management-panel-broadcast"
          role="tabpanel"
          aria-labelledby="platform-management-tab-broadcast"
        >
          <BroadcastManagementBoard />
        </div>
      ) : null}

      {effectiveSection === 'boss-db' ? (
        <div
          className="platform-management-panel"
          id="platform-management-panel-boss-db"
          role="tabpanel"
          aria-labelledby="platform-management-tab-boss-db"
        >
          <BossMasterSyncBoard />
        </div>
      ) : null}
    </>
  );
}
